/**
 * Payment Dialog - POS register checkout.
 *
 * One server order per checkout: opening the dialog creates the order from the
 * cart (priced by the server from the restaurant's menu and tax rate); every
 * method then pays THAT order.
 *   Card    -> Payment Hub sale on this register's terminal, polled to the verdict
 *   Cash    -> recorded with tendered amount / change
 *   Zelle, Venmo, Crypto, Gift Card, Loyalty -> confirmed by the cashier, recorded
 * Closing without paying cancels (abandons) the order so it never reaches the kitchen.
 * That is "Cancel an order": when the owner switched it on (Approval codes), a cashier
 * is asked for an owner's / manager's approval code first, in this dialog; without it
 * the order stays open to be paid. Refund / void after payment: every role that may
 * (the server's /api/dining/me), with the same approval when switched on.
 */

import { useUpdateHold } from '@/pwa/autoUpdate';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  CreditCard, Banknote, X, Check, AlertCircle,
  Loader2, DollarSign, Receipt, Smartphone,
  Gift, Star, Bitcoin, SplitSquareHorizontal
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import displayService from '../services/displayService';
import usePOSDisplayEvents from '../hooks/usePOSDisplayEvents';
import LoyaltyCheckoutModal from './LoyaltyCheckoutModal';
import GiftCardRedeemModal from './GiftCardRedeemModal';
import { checkoutLines } from '../lib/cartMoney';
import { useAuth } from '../context/AuthContext';
import { ReceiptActions, RefundBody } from '@/apps/dining/components/CheckoutExtras';
import SplitPaymentPanel from './SplitPaymentPanel';
import { useApproval } from '@/apps/dining/components/ApprovalDialog';
import { api as diningApi, deviceId } from '@/apps/dining/lib/api';

const API = process.env.REACT_APP_BACKEND_URL;
const TERMINAL_KEY = 'pos_payment_terminal_id';
const POLL_MS = 2000;

const formatCurrency = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
const fromCents = (c) => (c || 0) / 100;
const toCents = (d) => Math.round((Number(d) || 0) * 100);

const STATES = {
  PREPARING: 'preparing',       // creating the order
  IDLE: 'idle',
  PROCESSING: 'processing',
  WAITING_CARD: 'waiting_card',
  SUCCESS: 'success',
  FAILED: 'failed',
};

async function api(path, token, options = {}) {
  const res = await fetch(`${API}${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, 'X-Device-ID': deviceId(), ...(options.headers || {}) },
  });
  const body = await res.json().catch(() => null);
  if (!res.ok || !body) {
    throw new Error(typeof body?.detail === 'string' ? body.detail : 'The server is unavailable right now');
  }
  return body;
}

export default function PaymentDialog({
  open,
  onClose,
  paymentMethod, // 'card','cash','zelle','venmo','gift','loyalty','crypto','split'
  orderData,     // { items, customerName, orderType } - a new register order
  orderId,       // or: an existing order (a table's) to pay
  onSuccess,     // Callback when the order is paid
}) {
  const [state, setState] = useState(STATES.PREPARING);
  const [order, setOrder] = useState(null); // server order: { order_id, subtotal_cents, tax_cents, total_cents }
  const [cashReceived, setCashReceived] = useState('');
  const [changeCents, setChangeCents] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const [terminals, setTerminals] = useState([]);
  const [terminalId, setTerminalId] = useState(() => Number(localStorage.getItem(TERMINAL_KEY)) || null);
  const [payment, setPayment] = useState(null);
  const [paymentQRImages, setPaymentQRImages] = useState({});
  const [showLoyaltyModal, setShowLoyaltyModal] = useState(false);
  const [showGiftCardRedeem, setShowGiftCardRedeem] = useState(false);
  const [showRefund, setShowRefund] = useState(false);
  const [settings, setSettings] = useState(null);
  const [splitPaidCents, setSplitPaidCents] = useState(null);
  const splitRef = useRef(null);
  const pollRef = useRef(null);
  const paidRef = useRef(false);
  const { getToken, user } = useAuth();

  const { customerTip, displayConnected, reset: resetDisplayEvents } = usePOSDisplayEvents(open);
  useUpdateHold('pos-payment', !!open); // no app update while a payment is open
  // Tip already on the order (table checkout) plus one chosen on the customer display.
  const tipCents = (order?.tip_cents || 0) + toCents(customerTip);
  const dueCents = splitPaidCents ?? ((order?.due_cents || 0) + tipCents);
  const isSplit = paymentMethod === 'split';
  const [me, setMe] = useState(null);
  const [cancelNote, setCancelNote] = useState('');
  const approval = useApproval({ inline: true });
  // who may refund / void here: the server's answer for this sign-in (the ONE catalog)
  const canManage = me ? (me.actions || []).includes('payments.refund')
    : ['owner', 'manager'].includes(String(user?.role || '').toLowerCase());

  const stopPolling = () => {
    if (pollRef.current) clearTimeout(pollRef.current);
    pollRef.current = null;
  };

  // Create the server order when the dialog opens.
  useEffect(() => {
    if (!open || (!orderId && !orderData?.items?.length)) return undefined;
    let cancelled = false;
    paidRef.current = false;
    setShowRefund(false);
    setCancelNote('');
    diningApi.get('/dining/me').then(setMe).catch(() => setMe(null));
    setSplitPaidCents(null);
    setState(STATES.PREPARING);
    setOrder(null);
    setPayment(null);
    setCashReceived('');
    setChangeCents(0);
    setErrorMessage('');
    resetDisplayEvents();
    (async () => {
      try {
        const created = orderId ? await api(`/api/checkout/orders/${orderId}`, getToken()) : await api('/api/checkout/orders', getToken(), {
          method: 'POST',
          body: JSON.stringify({
            items: checkoutLines(orderData.items),
            order_type: orderData.orderType === 'dine_in' ? 'dine_in' : 'takeout',
            customer_name: orderData.customerName || null,
          }),
        });
        if (cancelled) return;
        setOrder(created);
        setState(STATES.IDLE);
        displayService.startCheckout({
          orderNumber: `#${created.order_id}`,
          items: orderData?.items || [],
          subtotal: fromCents(created.subtotal_cents),
          tax: fromCents(created.tax_cents),
          total: fromCents(created.due_cents + (created.tip_cents || 0)),
          paymentMethod: paymentMethod === 'card' ? 'CARD' : 'CASH',
          customerName: orderData?.customerName,
        });
      } catch (e) {
        if (cancelled) return;
        setErrorMessage(e.message);
        setState(STATES.FAILED);
      }
    })();
    return () => { cancelled = true; stopPolling(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Card (and split payment): this register's terminals.
  useEffect(() => {
    if (!open || !['card', 'split'].includes(paymentMethod)) return;
    api('/api/checkout/terminals?role=pos_register', getToken())
      .then(({ terminals: list }) => {
        setTerminals(list);
        if (!list.some(t => t.id === terminalId)) setTerminalId(list.length === 1 ? list[0].id : null);
      })
      .catch(e => setErrorMessage(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, paymentMethod]);

  // Split payment: the restaurant's tip / cash rules.
  useEffect(() => {
    if (!open || paymentMethod !== 'split') return;
    api('/api/checkout/settings', getToken()).then(setSettings).catch(() => setSettings(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, paymentMethod]);

  // Zelle / Venmo / Crypto QR images.
  useEffect(() => {
    const restaurantId = user?.restaurant_id;
    if (open && restaurantId && ['zelle', 'venmo', 'crypto', 'split'].includes(paymentMethod)) {
      fetch(`${API}/api/display/payment-qr/${restaurantId}`, { headers: { Authorization: `Bearer ${getToken()}` } })
        .then(r => (r.ok ? r.json() : {}))
        .then(d => { if (d.config) setPaymentQRImages(d.config); })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, paymentMethod]);

  // Paid: the dialog stays open on the receipt (print / text / e-mail) until the cashier taps Done.
  const finishPaid = useCallback(async (display) => {
    paidRef.current = true;
    setState(STATES.SUCCESS);
    displayService.showApproved(display);
    toast.success('Payment complete');
  }, []);

  const finishDone = () => {
    stopPolling();
    onSuccess?.();
    onClose();
  };

  const handleClose = async () => {
    if (state === STATES.PROCESSING) return;
    // Split payment: a card waiting, or parts already approved, are answered by the panel.
    if (isSplit && state === STATES.IDLE && splitRef.current?.requestClose()) return;
    if (state === STATES.WAITING_CARD) {
      await cancelCardPayment();
      return;
    }
    if (paidRef.current) { finishDone(); return; }
    if (approval.asking) return; // the approval panel answers (Approve / Cancel)
    stopPolling();
    if (order && !paidRef.current && !orderId) {
      // Cancel the order: when the owner switched "Cancel an order" on, a cashier needs an approval code
      const answer = await approval.run(
        (token) => diningApi.post(`/checkout/orders/${order.order_id}/abandon`, token ? { approval_token: token } : {}),
        { action: 'orders.cancel', entityId: order.order_id, what: `Order #${order.order_id}` },
      ).catch((e) => ({ error: e }));
      if (answer === null) {
        setCancelNote('Not cancelled - the order is still open. Take the payment, or ask an owner or manager to approve cancelling it.');
        return;
      }
      if (answer?.error && answer.error.status === 403) {
        setCancelNote(answer.error.message || 'This order cannot be cancelled from your sign-in.');
        return;
      }
      displayService.showCancelled();
    }
    onClose();
  };

  // ==================== CARD (Payment Hub) ====================
  const pollPayment = (paymentId) => {
    pollRef.current = setTimeout(async () => {
      try {
        const p = await api(`/api/checkout/payments/${paymentId}`, getToken());
        setPayment(p);
        if (!p.is_final) { pollPayment(paymentId); return; }
        if (p.status === 'approved' && !p.message?.startsWith('Partial')) {
          finishPaid({ card_type: p.card_brand || 'CARD', last_four: p.last4 || '', auth_code: p.auth_code || '', reference: `#${order.order_id}` });
        } else {
          setState(STATES.FAILED);
          const msg = p.message || `Payment ${p.status.replace('_', ' ')}`;
          setErrorMessage(msg);
          displayService.showDeclined(msg);
        }
      } catch {
        pollPayment(paymentId); // the server keeps the sale; keep asking
      }
    }, POLL_MS);
  };

  const handleCardPayment = async () => {
    if (!terminalId) { toast.error('Choose the terminal at this register'); return; }
    localStorage.setItem(TERMINAL_KEY, String(terminalId));
    setState(STATES.PROCESSING);
    setErrorMessage('');
    displayService.showWaitingForCard();
    try {
      const p = await api(`/api/checkout/orders/${order.order_id}/card`, getToken(), {
        method: 'POST', body: JSON.stringify({ terminal_id: terminalId, tip_cents: tipCents }),
      });
      setPayment(p);
      if (p.is_final) {
        setState(STATES.FAILED);
        setErrorMessage(p.message || 'Payment failed');
        return;
      }
      setState(STATES.WAITING_CARD);
      pollPayment(p.payment_id);
    } catch (e) {
      setState(STATES.FAILED);
      setErrorMessage(e.message);
      displayService.showError(e.message);
    }
  };

  const cancelCardPayment = async () => {
    if (!payment) return;
    try {
      const p = await api(`/api/checkout/payments/${payment.payment_id}/cancel`, getToken(), { method: 'POST' });
      setPayment(p);
      if (p.status === 'approved') return; // approved before the cancel arrived; the poll finishes it
      stopPolling();
      setState(STATES.FAILED);
      setErrorMessage('Payment cancelled');
      displayService.showCancelled();
    } catch (e) {
      toast.error(e.message);
    }
  };

  // ==================== CASH ====================
  const handleCashPayment = async () => {
    const tendered = toCents(cashReceived);
    if (tendered < dueCents) { toast.error('Insufficient cash received'); return; }
    setState(STATES.PROCESSING);
    try {
      const r = await api(`/api/checkout/orders/${order.order_id}/cash`, getToken(), {
        method: 'POST', body: JSON.stringify({ tendered_cents: tendered, tip_cents: tipCents }),
      });
      setChangeCents(r.change_cents);
      finishPaid({ card_type: 'CASH', reference: `#${order.order_id}` });
    } catch (e) {
      setState(STATES.FAILED);
      setErrorMessage(e.message);
    }
  };

  // ==================== CASHIER-CONFIRMED METHODS ====================
  const recordManual = async (method, reference) => {
    setState(STATES.PROCESSING);
    try {
      await api(`/api/checkout/orders/${order.order_id}/manual`, getToken(), {
        method: 'POST', body: JSON.stringify({ method, reference: reference || null, tip_cents: tipCents }),
      });
      finishPaid({ card_type: method.replace('_', ' ').toUpperCase(), reference: reference || `#${order.order_id}` });
    } catch (e) {
      setState(STATES.FAILED);
      setErrorMessage(e.message);
    }
  };

  // ==================== RENDER ====================
  const renderStatus = () => {
    if (state === STATES.PREPARING) {
      return (
        <div className="text-center py-12">
          <Loader2 className="w-12 h-12 mx-auto mb-4 text-blue-500 animate-spin" />
          <p className="text-white">Preparing order...</p>
        </div>
      );
    }
    if (state === STATES.PROCESSING) {
      return (
        <div className="text-center py-12">
          <Loader2 className="w-16 h-16 mx-auto mb-4 text-blue-500 animate-spin" />
          <p className="text-white text-lg">Processing...</p>
        </div>
      );
    }
    if (state === STATES.SUCCESS) {
      return (
        <div className="text-center py-12" data-testid="payment-success">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-500/20 flex items-center justify-center">
            <Check className="w-10 h-10 text-green-500" />
          </div>
          <p className="text-green-500 text-2xl font-bold mb-2">Payment Complete!</p>
          <p className="text-white text-xl">{formatCurrency(fromCents(dueCents))}</p>
          {changeCents > 0 && (
            <div className="bg-yellow-500/20 rounded-lg p-4 inline-block mt-4">
              <p className="text-yellow-500 text-sm">Change Due</p>
              <p className="text-yellow-500 text-3xl font-bold">{formatCurrency(fromCents(changeCents))}</p>
            </div>
          )}
          {order && (
            <div className="mt-6 text-left space-y-3">
              {showRefund ? (
                <RefundBody dark scope={{ kind: 'order', id: order.order_id }} onDone={() => setShowRefund(false)} />
              ) : (
                <>
                  <ReceiptActions dark kind="order" id={order.order_id} title={`Receipt · order #${order.order_id}`} />
                  {canManage && (
                    <button type="button" onClick={() => setShowRefund(true)} data-testid="register-refund-open"
                      className="w-full h-11 rounded-xl border border-red-500/40 text-red-300 text-sm font-semibold hover:bg-red-500/10">
                      Refund or void this payment
                    </button>
                  )}
                </>
              )}
              <Button className="w-full h-12 bg-slate-100 text-slate-900 hover:bg-white" onClick={finishDone} data-testid="payment-done-btn">Done · next order</Button>
            </div>
          )}
        </div>
      );
    }
    if (state === STATES.FAILED) {
      return (
        <div className="text-center py-8" data-testid="payment-failed">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-500/20 flex items-center justify-center">
            <AlertCircle className="w-10 h-10 text-red-500" />
          </div>
          <p className="text-red-500 text-xl font-bold mb-2">Payment Failed</p>
          <p className="text-gray-400 mb-6">{errorMessage}</p>
          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" onClick={handleClose} data-testid="payment-cancel-after-fail">Close</Button>
            {order && (
              <Button className="flex-1 bg-blue-600" onClick={() => { setState(STATES.IDLE); setErrorMessage(''); }} data-testid="payment-retry-btn">
                Try Again
              </Button>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  const renderTotals = () => (
    <div className="bg-slate-800/60 rounded-lg p-3 mb-4 text-sm space-y-1" data-testid="order-totals">
      <div className="flex justify-between text-gray-400"><span>Subtotal</span><span>{formatCurrency(fromCents(order.subtotal_cents))}</span></div>
      {order.discount_cents > 0 && <div className="flex justify-between text-gray-400"><span>Discount</span><span>-{formatCurrency(fromCents(order.discount_cents))}</span></div>}
      <div className="flex justify-between text-gray-400"><span>Tax{order.tax_rate_percent != null ? ` (${order.tax_rate_percent}%)` : ''}</span><span>{formatCurrency(fromCents(order.tax_cents))}</span></div>
      {tipCents > 0 && <div className="flex justify-between text-emerald-400"><span>Tip</span><span>{formatCurrency(fromCents(tipCents))}</span></div>}
      <div className="flex justify-between text-white font-semibold text-base pt-1 border-t border-slate-700"><span>Total</span><span>{formatCurrency(fromCents(dueCents))}</span></div>
    </div>
  );

  const renderCardContent = () => {
    if (state === STATES.WAITING_CARD) {
      return (
        <div className="text-center py-8" data-testid="card-waiting">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-yellow-500/20 flex items-center justify-center animate-pulse">
            <CreditCard className="w-10 h-10 text-yellow-500" />
          </div>
          <p className="text-white text-xl mb-2">Insert or Tap Card</p>
          <p className="text-gray-400 mb-1">{formatCurrency(fromCents(dueCents))} sent to the terminal</p>
          <div className="flex items-center justify-center gap-2 text-yellow-500/80 text-xs mb-6">
            <Loader2 className="w-3 h-3 animate-spin" /> Waiting for the terminal
          </div>
          <Button variant="outline" className="border-slate-700 text-slate-300" onClick={cancelCardPayment} data-testid="card-cancel-btn">
            <X className="w-4 h-4 mr-2" /> Cancel Payment
          </Button>
        </div>
      );
    }
    return (
      <div className="py-2" data-testid="card-payment-idle">
        {renderTotals()}
        {terminals.length === 0 ? (
          <p className="text-sm text-amber-400 mb-4" data-testid="no-register-terminal">
            No terminal is assigned to the POS register. The owner assigns one in Payment Settings → Payment Terminals.
          </p>
        ) : terminals.length > 1 && (
          <div className="mb-4">
            <p className="text-sm text-gray-400 mb-2">Terminal at this register</p>
            <div className="grid gap-2">
              {terminals.map(t => (
                <button key={t.id} onClick={() => setTerminalId(t.id)}
                  className={`text-left p-3 rounded-lg border ${terminalId === t.id ? 'border-blue-500 bg-blue-500/10' : 'border-slate-700'}`}
                  data-testid={`terminal-option-${t.id}`}>
                  <p className="text-white text-sm">{t.station || t.name || `Terminal ${t.hub_terminal_id}`}</p>
                  <p className="text-xs text-gray-500">{t.equipment || 'Card terminal'}{t.serial ? ` · ${t.serial}` : ''}</p>
                </button>
              ))}
            </div>
          </div>
        )}
        <Button className="w-full h-14 bg-blue-600 hover:bg-blue-700" onClick={handleCardPayment}
          disabled={!terminalId} data-testid="card-start-btn">
          <CreditCard className="w-5 h-5 mr-2" /> Send to Terminal
        </Button>
      </div>
    );
  };

  const renderCashContent = () => {
    const received = toCents(cashReceived);
    const total = fromCents(dueCents);
    const quickAmounts = [Math.ceil(total), Math.ceil(total / 5) * 5, Math.ceil(total / 10) * 10, Math.ceil(total / 20) * 20]
      .filter((v, i, a) => a.indexOf(v) === i).slice(0, 4);
    return (
      <div className="py-2">
        {renderTotals()}
        <label className="text-sm text-gray-400 mb-2 block">Cash Received</label>
        <div className="relative mb-4">
          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
          <Input type="number" step="0.01" min="0" value={cashReceived} onChange={(e) => setCashReceived(e.target.value)}
            className="pl-10 h-14 text-2xl bg-slate-800 border-slate-700 text-white" placeholder="0.00" autoFocus />
        </div>
        <div className="grid grid-cols-4 gap-2 mb-4">
          {quickAmounts.map(amount => (
            <Button key={amount} variant="outline" className="bg-slate-800 border-slate-700 text-white hover:bg-slate-700"
              onClick={() => setCashReceived(amount.toString())}>${amount}</Button>
          ))}
        </div>
        {received >= dueCents && received > 0 && (
          <div className="bg-green-500/20 rounded-lg p-4 mb-4 text-center">
            <p className="text-green-400 text-sm">Change Due</p>
            <p className="text-green-400 text-2xl font-bold">{formatCurrency(fromCents(received - dueCents))}</p>
          </div>
        )}
        <Button className="w-full h-14 bg-green-600 hover:bg-green-700" onClick={handleCashPayment} disabled={received < dueCents}>
          <Receipt className="w-5 h-5 mr-2" /> Complete Payment
        </Button>
      </div>
    );
  };

  const MANUAL_META = {
    zelle:  { label: 'Zelle',  method: 'zelle',  icon: DollarSign, color: 'bg-violet-500/20', textColor: 'text-violet-500', btnColor: 'bg-violet-600 hover:bg-violet-700', qrKey: 'zelle_qr_url' },
    venmo:  { label: 'Venmo',  method: 'venmo',  icon: Smartphone, color: 'bg-cyan-500/20',   textColor: 'text-cyan-500',   btnColor: 'bg-cyan-600 hover:bg-cyan-700',   qrKey: 'venmo_qr_url' },
    crypto: { label: 'Crypto', method: 'crypto', icon: Bitcoin,    color: 'bg-orange-500/20', textColor: 'text-orange-500', btnColor: 'bg-orange-600 hover:bg-orange-700', qrKey: 'crypto_qr_url' },
  };

  const renderManualContent = () => {
    if (paymentMethod === 'gift') {
      return (
        <div className="text-center py-2" data-testid="gift-idle">
          {renderTotals()}
          <Button className="w-full h-14 bg-purple-600 hover:bg-purple-700" onClick={() => setShowGiftCardRedeem(true)} data-testid="gift-idle-redeem-btn">
            <Gift className="w-5 h-5 mr-2" /> Redeem Gift Card
          </Button>
          <GiftCardRedeemModal
            open={showGiftCardRedeem}
            onClose={() => setShowGiftCardRedeem(false)}
            orderTotal={fromCents(dueCents)}
            orderId={String(order.order_id)}
            onApply={(result) => {
              setShowGiftCardRedeem(false);
              if (result.split_tender_remaining > 0) {
                toast.info(`${formatCurrency(result.split_tender_remaining)} remaining — the gift card does not cover this order`);
                return;
              }
              recordManual('gift_card', result.card_code);
            }}
          />
        </div>
      );
    }
    if (paymentMethod === 'loyalty') {
      return (
        <div className="text-center py-2" data-testid="loyalty-idle">
          {renderTotals()}
          <Button className="w-full h-14 bg-amber-600 hover:bg-amber-700" onClick={() => setShowLoyaltyModal(true)} data-testid="loyalty-idle-search-btn">
            <Star className="w-5 h-5 mr-2" /> Search Customer
          </Button>
          <LoyaltyCheckoutModal
            open={showLoyaltyModal}
            onClose={() => setShowLoyaltyModal(false)}
            orderTotal={fromCents(dueCents)}
            onApply={(result) => {
              setShowLoyaltyModal(false);
              recordManual('loyalty', `${result.points_redeemed} pts`);
            }}
          />
        </div>
      );
    }
    const meta = MANUAL_META[paymentMethod] || MANUAL_META.zelle;
    const Icon = meta.icon;
    const qrUrl = paymentQRImages[meta.qrKey];
    const fullQrUrl = qrUrl ? (qrUrl.startsWith('/api/') ? `${API}${qrUrl}` : qrUrl) : null;
    return (
      <div className="text-center py-2" data-testid={`${paymentMethod}-idle`}>
        {renderTotals()}
        {fullQrUrl ? (
          <img src={fullQrUrl} alt={`${meta.label} QR Code`} className="mx-auto max-h-48 rounded-lg border border-gray-700 mb-3" data-testid={`${paymentMethod}-qr-image`} />
        ) : (
          <div className={`w-20 h-20 mx-auto mb-4 rounded-full ${meta.color} flex items-center justify-center`}>
            <Icon className={`w-10 h-10 ${meta.textColor}`} />
          </div>
        )}
        <p className="text-gray-400 text-sm mb-4">Confirm only after you see the {meta.label} payment received.</p>
        <Button className={`w-full h-14 ${meta.btnColor}`} onClick={() => recordManual(meta.method)} data-testid={`${paymentMethod}-confirm-btn`}>
          <Check className="w-5 h-5 mr-2" /> Confirm Payment Received
        </Button>
      </div>
    );
  };

  const renderSplitContent = () => (
    <SplitPaymentPanel
      ref={splitRef}
      order={order}
      token={getToken}
      terminals={terminals}
      terminalId={terminalId}
      onTerminal={(id) => { setTerminalId(id); localStorage.setItem(TERMINAL_KEY, String(id)); }}
      tipsEnabled={!!settings?.tips_enabled}
      tipPresets={settings?.tip_presets || []}
      tipOnTerminal={settings?.terminal_tip_mode === 'terminal'}
      settings={settings}
      qr={paymentQRImages}
      onCancel={handleClose}
      canManage={canManage}
      onWaitingCard={(waiting) => { if (waiting) displayService.showWaitingForCard(); }}
      onPaid={(display, view) => {
        setSplitPaidCents((view?.base || 0) + (view?.payments || []).reduce((n, p) => n + (p.voided ? 0 : p.tip), 0));
        finishPaid(display);
      }}
      onLeave={() => {
        // the order stays part-paid with its balance due: nothing is abandoned
        stopPolling();
        toast.info(`Order #${order.order_id} kept with its balance due`);
        onSuccess?.();
        onClose();
      }}
    />
  );

  const renderContent = () => {
    if (approval.asking) return <div className="max-h-[70vh] overflow-y-auto" data-testid="register-approval">{approval.dialog}</div>;
    const status = renderStatus();
    if (status) return status;
    if (isSplit) return renderSplitContent();
    if (paymentMethod === 'card' || state === STATES.WAITING_CARD) return renderCardContent();
    if (paymentMethod === 'cash') return renderCashContent();
    return renderManualContent();
  };

  const TITLE_MAP = {
    card: 'Card Payment', cash: 'Cash Payment', zelle: 'Zelle Payment', venmo: 'Venmo Payment',
    gift: 'Gift Card Payment', loyalty: 'Loyalty Points', crypto: 'Crypto Payment', split: 'Split payment',
  };
  const ICON_MAP = {
    card: <CreditCard className="w-5 h-5 text-blue-500" />,
    cash: <Banknote className="w-5 h-5 text-green-500" />,
    zelle: <DollarSign className="w-5 h-5 text-violet-500" />,
    venmo: <Smartphone className="w-5 h-5 text-cyan-500" />,
    gift: <Gift className="w-5 h-5 text-amber-500" />,
    loyalty: <Star className="w-5 h-5 text-pink-500" />,
    crypto: <Bitcoin className="w-5 h-5 text-orange-500" />,
    split: <SplitSquareHorizontal className="w-5 h-5 text-sky-400" />,
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className={`bg-slate-900 border-slate-800 text-white ${isSplit ? 'max-w-lg max-h-[92vh] overflow-y-auto' : 'max-w-md'}`} data-testid={isSplit ? 'split-dialog' : undefined}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {ICON_MAP[paymentMethod] || ICON_MAP.card}
            {TITLE_MAP[paymentMethod] || 'Payment'}
            {order && <span className="text-xs text-slate-400 font-normal">Order #{order.order_id}</span>}
            {displayConnected && (
              <span className="ml-auto flex items-center gap-1.5 text-xs text-slate-400 font-normal" data-testid="display-connected-badge">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" /> Display
              </span>
            )}
          </DialogTitle>
        </DialogHeader>
        {cancelNote && !approval.asking && (
          <p className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-200" role="status" data-testid="register-cancel-note">{cancelNote}</p>
        )}
        {renderContent()}
      </DialogContent>
    </Dialog>
  );
}

